import React, { useEffect, useState } from 'react';
import { withFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Form, Label, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import Icon from 'shared/components/Icon';

import styles from './SelectBatchForm.styles';

const BatchGroup = ({ label, batches, selectedBilling, onSubmit, showDeleteButton, openModal }) => (
  <>
    <p className="mt-3 mb-2 p-2 font-weight-bold group-label">{label}</p>
    {batches.map(batch => {
      const description = batch.createdBy ? `${batch.createdAt}, ${batch.createdBy}` : batch.createdAt;
      return (
        <div className="batch-list-item" key={batch.id}>
          <div className="form-check input-wrap mb-2">
            <Input
              checked={batch.id === selectedBilling}
              className="form-check-input"
              id={`selectedBilling-${batch.id}`}
              name="selectedBilling"
              onChange={() => onSubmit(batch.id)}
              placeholder="Selected Batch"
              type="radio"
            />
            <Label className="form-check-label font-weight-bold" htmlFor={`selectedBilling-${batch.id}`}>
              {batch.name}
            </Label>
            <p className="batch-description">{description}</p>
          </div>
          {showDeleteButton(batch) && (
            <div onClick={() => openModal(batch)}>
              <Icon className="trash-can" icon="delete" />
            </div>
          )}
        </div>
      );
    })}
  </>
);

const BATCH_GROUPS = [
  { key: 'created', label: 'Created' },
  { key: 'generated', label: 'Generated' },
  { key: 'finalized', label: 'Finalized' }
];

let SelectBatchFormNew = ({
  billing,
  handleSubmit,
  onSubmit,
  selectedBilling,
  setFieldValue,
  billingDeleteAllPreflight,
  billingDeleteAllCustomers,
  recentlyDeleted,
  isProcessing,
  isLoading
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [whichBatch, setWhichBatch] = useState({});

  useEffect(() => {
    setFieldValue('selectedBilling', selectedBilling);
  }, [selectedBilling, setFieldValue]);

  if (!billing.length) return null;

  const openModal = batch => {
    setIsOpen(true);
    setWhichBatch(batch);
  };

  const closeModal = () => {
    setIsOpen(false);
    setWhichBatch({});
  };

  const preflightBatch = () => {
    billingDeleteAllPreflight(whichBatch.id);
    closeModal();
  };

  const replaceLastPathSegment = (newId, location) => {
    const pathSegments = location.pathname.split('/');
    if (!pathSegments.length) return;

    if (newId) {
      pathSegments[pathSegments.length - 1] = newId;
    } else {
      pathSegments.pop();
      setFieldValue('selectedBilling', null);
    }
    return pathSegments.join('/');
  };

  const deleteBatch = () => {
    if (!whichBatch.isFinalized) {
      const earliestBatch = billing?.find(batch => batch.id !== whichBatch.id);
      const redirectPath = replaceLastPathSegment(earliestBatch?.id, location);
      billingDeleteAllCustomers(whichBatch.id, whichBatch.name);
      history.push(redirectPath);
      closeModal();
    }
  };

  const showDeleteButton = batch =>
    !(isProcessing || batch.isFinalized || batch.isGenerated || recentlyDeleted.includes(batch.id) || isLoading.status);

  const groupedBatches = billing
    .filter(b => !recentlyDeleted.includes(b.id))
    .reduce(
      (groups, batch) => {
        const key = batch.isFinalized ? 'finalized' : batch.isGenerated ? 'generated' : 'created';
        groups[key].push(batch);
        return groups;
      },
      { created: [], generated: [], finalized: [] }
    );

  return (
    <div css={styles}>
      <hr />
      <div className="pl-4 pr-4">
        <h5>
          <strong>Batch</strong>
        </h5>
        <div className="select-batch-form mb-2">
          <Form autoComplete="nope" onSubmit={handleSubmit}>
            {BATCH_GROUPS.map(({ key, label }) =>
              groupedBatches[key].length > 0 ? (
                <BatchGroup
                  key={key}
                  label={label}
                  batches={groupedBatches[key]}
                  selectedBilling={selectedBilling}
                  onSubmit={onSubmit}
                  showDeleteButton={showDeleteButton}
                  openModal={openModal}
                />
              ) : null
            )}
          </Form>
        </div>
      </div>

      <Modal isOpen={isOpen}>
        <ModalHeader>Delete batch {whichBatch.name}?</ModalHeader>
        <ModalBody>All non-finalized invoices in the batch will be deleted.</ModalBody>
        <ModalFooter>
          <Button color="link" onClick={closeModal}>
            Cancel
          </Button>
          <Button onClick={preflightBatch}>No, Just Run Preflight Check</Button>
          <Button onClick={deleteBatch}>Yes, Delete</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

SelectBatchFormNew = withFormik({
  mapPropsToValues: ({ selectedBilling }) => ({ selectedBilling })
})(SelectBatchFormNew);

export default SelectBatchFormNew;
